/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BulkExtractionPayload } from '../models/BulkExtractionPayload';
import type { ExtractionJobRead_Input } from '../models/ExtractionJobRead_Input';
import type { ExtractionJobRead_Output } from '../models/ExtractionJobRead_Output';
import type { ExtractionPayload } from '../models/ExtractionPayload';
import type { GetExtractionResultsPayload } from '../models/GetExtractionResultsPayload';
import type { GetSucceededExtractionPayload } from '../models/GetSucceededExtractionPayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ExtractionService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Start Extraction
     * @param entityId
     * @param requestBody
     * @returns ExtractionJobRead_Output Successful Response
     * @throws ApiError
     */
    public startExtraction(
        entityId: string,
        requestBody: ExtractionPayload,
    ): CancelablePromise<ExtractionJobRead_Output> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/extraction/start-extraction',
            query: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Start Extractions
     * Kick off extraction for each file and return the current job per file.
     *
     * Intentionally does not collect results inline. For an existing job that has not yet
     * produced a ``response_s3_key`` we return the job as-is (STARTED / IN_PROGRESS) rather
     * than draining Textract and uploading to S3 within the request. Result collection is
     * handled lazily by the polling ``list-extraction-results`` -> ``check_extraction_status``
     * path, which the frontend already drives on an interval. This keeps the request cheap and
     * its latency independent of document size and batch size.
     * @param entityId
     * @param requestBody
     * @returns ExtractionJobRead_Input Successful Response
     * @throws ApiError
     */
    public startExtractions(
        entityId: string,
        requestBody: BulkExtractionPayload,
    ): CancelablePromise<Record<string, ExtractionJobRead_Input>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/extraction/start-extractions',
            query: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Extraction Result
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public getExtractionResult(
        entityId: string,
        requestBody: ExtractionPayload,
    ): CancelablePromise<(ExtractionJobRead_Input | null)> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/extraction/get-extraction-result',
            query: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Succeeded Extraction
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public getSucceededExtraction(
        entityId: string,
        requestBody: GetSucceededExtractionPayload,
    ): CancelablePromise<(ExtractionJobRead_Input | null)> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/extraction/get-succeeded-extraction',
            query: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Extraction Results
     * @param entityId
     * @param requestBody
     * @returns ExtractionJobRead_Input Successful Response
     * @throws ApiError
     */
    public listExtractionResults(
        entityId: string,
        requestBody: GetExtractionResultsPayload,
    ): CancelablePromise<Array<ExtractionJobRead_Input>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/extraction/list-extraction-results',
            query: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
